<template>
	<content-layout>
		<template #header>
			Pending locations
		</template>
		<template #header-right>
			<el-dropdown @command="handleSortBy">
        <span class="el-dropdown-link">
          Sort by: {{ selectedSortByName }}<i class="el-icon-arrow-down el-icon--right"></i>
        </span>
				<el-dropdown-menu slot="dropdown">
					<el-dropdown-item v-for="sortByOption of sortByOptions"
					                  :command="sortByOption.action"
					                  :key="sortByOption.name">{{ sortByOption.name }}
					</el-dropdown-item>
				</el-dropdown-menu>
			</el-dropdown>
		</template>

		<div v-if="locations.length">
			<location-list>
				<location-list-item v-if="!loading" v-for="location in locations"
				                    :location-id="location._id"
				                    :badges="getAllBadges(location._id)"
				                    :thumbnail="location.thumbnail"
				                    :reference="location.locationReference"
				                    :submitted-at="formatDate(location.registrationCompletedDate)"
				                    :address="location.addressOne"
				                    :postcode="location.postcode"
				                    :county="location.county"
				                    :image-count="location.imageCount"
				                    :submitted-count="location.submittedCount"
				                    @edit="goToLocation(location.locationReference)"
				                    @delete="deleteLocation(location._id)"
				                    @reactivate="reActivate(location._id, location.user)"
				                    @badgeChange="badge => onBadgeChange(location._id, badge)"/>
			</location-list>


			<div class="block pagination" style="text-align: center">
				<el-pagination
					layout="prev, pager, next"
					:page-size="perPage"
					:total="sum"
					:current-page.sync="currentPage"
					@current-change="pageChange">
				</el-pagination>
			</div>
		</div>

	</content-layout>

</template>

<script>
import auth from '../mixins/auth';
import * as queryString from 'query-string';
import LocationListItem from "../components/location-list-item";
import LocationList from "../components/location-list";

const moment = require('moment');

export default {
	name: "PendingLocations",
	components: {LocationList, LocationListItem},
	mixins: [auth],

	data() {
		return {
			locations: [],
			loading: false,
			perPage: 10,
			sum: null,
			currentPage: 1,
			sortBy: 'latest',
			sortByOptions: [
				{
					name: 'Submitted - latest',
					action: 'latest'
				},
				{
					name: 'Submitted - oldest',
					action: 'oldest'
				},
				{
					name: 'Photo count - highest',
					action: 'photoHighest'
				},
				{
					name: 'Photo count - lowest',
					action: 'photoLowest'
				},
				{
					name: 'Priority',
					action: 'priority'
				},
				{
					name: 'Accepted',
					action: 'accepted'
				},
				{
					name: 'Rejected',
					action: 'rejected'
				},
				{
					name: 'More photos',
					action: 'morePhotos'
				}
			]
		}
	},

	computed: {
		selectedSortByName() {
			return this.sortByOptions.find(option => option.action == this.sortBy).name;
		}
	},

	methods: {

		async handleSortBy(command) {
			this.currentPage = 1;
			this.sortBy = command;
			this.$router.push({
				query: {
					sort: this.sortBy
				}
			});
			await this.queryLocations(this.perPage, 0, this.sortBy);
		},

		onBadgeChange(locationId, badge) {

			let location = this.locations.find(location => location._id == locationId);
			for (const key of Object.keys(badge)) {
				location[key] = badge[key];
			}
		},

		getAllBadges(locationId) {
			let location = this.locations.find(location => location._id == locationId);
			let {priorityBadge, acceptBadge, rejectBadge, morePhotosBadge} = location;
			return {
				priorityBadge: priorityBadge,
				acceptBadge: acceptBadge,
				rejectBadge: rejectBadge,
				morePhotosBadge: morePhotosBadge
			}
		},

		// reactivating a location because it needs changes made to it
		async reActivate(locationId, userId) {

			// First check if user has any pending locations currently registered and unfinished
			const pendingLocation = await this.axios.get(`${process.env.VUE_APP_ROOT_API}/api/locations/unfinishedRegisterLocation/${userId}`, {headers: this.headers}).then(response => {
				return response.data;
			});
			if (pendingLocation) {
				this.$message('User is currently in the middle of registering a location. Please tell them to finish registering it before this location can be re-activated.');
			} else {
				await this.axios.patch(`${process.env.VUE_APP_ROOT_API}/api/locations/${locationId}`,
					{
						registrationComplete: false,
						registrationStage: 0
					}
					, {headers: this.headers});

				this.$notify({
					title: 'Success',
					message: 'Location re-activated.',
					type: 'success'
				});

				this.locations = this.locations.filter((location) => {
					return location._id !== locationId
				});

			}

		},

		async pageChange(newPage) {
			this.loading = true;

			const query = Object.assign({}, this.$route.query);
			query.page = newPage;

			this.$router.push({
				query: query
			});

			await this.queryLocations(this.perPage, (newPage - 1) * this.perPage);
			this.loading = false;
		},
		async deleteLocation(locationId) {
			try {
				await this.$confirm('This will permanently delete the location. Continue?', 'Warning', {
					confirmButtonText: 'OK',
					cancelButtonText: 'Cancel',
					type: 'warning'
				});

				this.loading = true;
				const response = await this.axios.delete(process.env.VUE_APP_ROOT_API + '/api/locations/' + locationId,
					{headers: this.headers});

				this.locations = this.locations.filter((location) => {
					return location._id !== locationId
				});

				this.loading = false;

				this.$message({
					message: 'Location deleted.',
					type: 'success'
				});


			} catch (err) {
				console.log(err);
			}

		},
		formatName({firstName, lastName}) {
			if (firstName && lastName) {
				return firstName + ' ' + lastName;
			} else {
				return '';
			}
		},

		formatDate(date) {
			if (!!date) {
				return moment(date).format('DD/MM/YYYY HH:mm');
			}
		},

		async queryLocations(limit, skip, sort = this.sortBy) {
			try {
				this.loading = true;
				await this.axios.get(process.env.VUE_APP_ROOT_API + '/api/locations/pendingLocations?limit=' + limit + '&skip=' + skip + '&sort=' + sort,
					{headers: this.headers})
					.then(response => {
						this.sum = response.data.sum;
						this.locations = response.data.locations;
					});
				this.loading = false;
			} catch (err) {
				console.log(err);
			}

		},

		goToLocation(reference) {
			this.$router.push('/locations/' + reference + '?' + queryString.stringify(this.searchQuery));
		}
	},

	async created() {
		this.loading = true;

		let page;

		if (this.$route.query.page) {
			this.currentPage = this.$route.query.page;
			page = this.$route.query.page - 1
		} else {
			page = 0
		}

		if(this.$route.query.sort) {
			this.sortBy = this.$route.query.sort;
			await this.queryLocations(this.perPage, page * this.perPage, this.$route.query.sort);
		} else {
			await this.queryLocations(this.perPage, page * this.perPage);
		}


		this.loading = false;
	}
}
</script>

<style>

</style>

<style scoped>
.el-dropdown-link {
	cursor: pointer;
}

.el-icon-arrow-down {
	font-size: 12px;
}

.search-bar {
	border-bottom: 1px solid rgba(230, 230, 230, 0.6);
}

.pagination {
	margin-top: 20px;
}
</style>



// WEBPACK FOOTER //
// src/pages/pending-locations.vue